/* ── HEIC-to-JPEG converter ────────────────────────────────────────────
 *  Dynamically loads heic2any (WASM) from CDN only when a HEIC file is
 *  detected.  Returns the original file unchanged for non-HEIC images.
 */
async function convertHeicToJpegIfNeeded(file) {
  var isHeic = /\.heic$/i.test(file.name) ||
               /\.heif$/i.test(file.name) ||
               file.type === 'image/heic' ||
               file.type === 'image/heif';
  if (!isHeic) return file;

  // Dynamically load heic2any from CDN (only on first HEIC encounter)
  if (typeof heic2any === 'undefined') {
    await new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js';
      s.onload = resolve;
      s.onerror = function () { reject(new Error('Failed to load HEIC converter')); };
      document.head.appendChild(s);
    });
  }

  var result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
  // heic2any returns Blob | Blob[] — normalise to single blob
  var jpegBlob = Array.isArray(result) ? result[0] : result;
  var jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([jpegBlob], jpegName, { type: 'image/jpeg' });
}

class VirtualTryOn {
  constructor(btn) {
    this.btn = btn;
    this.apiUrl = btn.dataset.apiUrl || 'http://localhost:3001';
    this.handle = btn.dataset.productHandle;
    this.variantId = btn.dataset.variantId;
    this.productTitle = btn.dataset.productTitle;
    this.productImage = btn.dataset.productImage;

    this.fullBodyFile = null;
    this.selfieFile = null;
    this.resultUrl = null;
    this.generating = false;

    this.drawer = document.getElementById('vto-drawer');
    if (!this.drawer) return;
    this.drawer.__vto__ = this;

    this.bind();
  }

  bind() {
    this.btn.addEventListener('click', () => this.open());

    this.drawer.querySelector('.vto-backdrop').addEventListener('click', () => this.close());
    this.drawer.querySelector('.vto-close').addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    const fullBodyZone = this.drawer.querySelector('.vto-dropzone--fullbody');
    const selfieZone = this.drawer.querySelector('.vto-dropzone--selfie');

    this.setupDropzone(fullBodyZone, 'fullbody');
    this.setupDropzone(selfieZone, 'selfie');
    this.setupPicker();

    this.drawer.querySelector('.vto-generate-btn').addEventListener('click', () => this.generate());
  }

  setupDropzone(zone, type) {
    var cameraInput = zone.querySelector('.vto-input--camera');
    var fileInput = zone.querySelector('.vto-input--file');

    // Click on the zone opens the PhotoPicker modal
    zone.addEventListener('click', function () {
      this.openPicker(type);
    }.bind(this));

    // Camera input (triggered by PhotoPicker "Take Photo")
    cameraInput.addEventListener('change', async (e) => {
      var file = e.target.files[0];
      if (file) await this.setFile(type, file, zone);
    });

    // File/gallery input (triggered by PhotoPicker "Choose from Gallery")
    fileInput.addEventListener('change', async (e) => {
      var file = e.target.files[0];
      if (file) await this.setFile(type, file, zone);
    });

    // Drag-and-drop on the whole zone
    zone.addEventListener('dragover', function (e) {
      e.preventDefault();
      zone.classList.add('vto-dropzone--hover');
    });

    zone.addEventListener('dragleave', function () {
      zone.classList.remove('vto-dropzone--hover');
    });

    zone.addEventListener('drop', async function (e) {
      e.preventDefault();
      zone.classList.remove('vto-dropzone--hover');
      var file = e.dataTransfer.files[0];
      // Accept images by MIME type (image/jpeg, image/png, image/heic, etc.)
      // OR by file extension (.heic/.HEIC — iOS sometimes reports HEIC as
      // application/octet-stream in drag-and-drop).
      if (file && (file.type.startsWith('image/') || /\.heic$/i.test(file.name) || /\.heif$/i.test(file.name))) {
        await this.setFile(type, file, zone);
      }
    }.bind(this));
  }

  /* ─── PhotoPicker Modal ─── */

  get picker() {
    return document.getElementById('vto-picker');
  }

  get pickerTitle() {
    return this.picker ? this.picker.querySelector('#vto-picker-title') : null;
  }

  openPicker(slot) {
    this._pickerSlot = slot;
    var picker = this.picker;
    if (!picker) return;
    var title = slot === 'fullbody' ? 'Add Full Body Photo' : 'Add Selfie';
    if (this.pickerTitle) this.pickerTitle.textContent = title;
    picker.setAttribute('aria-hidden', 'false');
    picker.classList.add('vto-picker--open');
    document.body.style.overflow = 'hidden';
  }

  closePicker() {
    var picker = this.picker;
    if (!picker) return;
    picker.setAttribute('aria-hidden', 'true');
    picker.classList.remove('vto-picker--open');
    document.body.style.overflow = '';
    this._pickerSlot = null;
  }

  setupPicker() {
    var picker = this.picker;
    if (!picker) return;

    // Close on backdrop click or Cancel
    picker.querySelectorAll('[data-picker-close]').forEach(function (el) {
      el.addEventListener('click', this.closePicker.bind(this));
    }.bind(this));

    // "Take Photo" button — triggers the camera input for the active slot
    picker.querySelector('[data-picker-camera]').addEventListener('click', function () {
      var slot = this._pickerSlot;
      if (!slot) return;
      this.closePicker();
      // Find the camera input in the matching dropzone
      var zone = this.drawer.querySelector('.vto-dropzone--' + slot);
      if (zone) {
        var cam = zone.querySelector('.vto-input--camera');
        if (cam) cam.click();
      }
    }.bind(this));

    // "Choose from Gallery" button — triggers the file input for the active slot
    picker.querySelector('[data-picker-gallery]').addEventListener('click', function () {
      var slot = this._pickerSlot;
      if (!slot) return;
      this.closePicker();
      var zone = this.drawer.querySelector('.vto-dropzone--' + slot);
      if (zone) {
        var file = zone.querySelector('.vto-input--file');
        if (file) file.click();
      }
    }.bind(this));

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && this.picker && this.picker.classList.contains('vto-picker--open')) {
        this.closePicker();
      }
    }.bind(this));
  }

  async setFile(type, file, zone) {
    // Convert HEIC to JPEG before storing/previewing
    var converted = await convertHeicToJpegIfNeeded(file);
    if (type === 'fullbody') this.fullBodyFile = converted;
    else this.selfieFile = converted;

    var preview = URL.createObjectURL(converted);
    zone.classList.add('vto-dropzone--filled');

    var existingImg = zone.querySelector('img');
    if (existingImg) existingImg.src = preview;
    else {
      var img = document.createElement('img');
      img.src = preview;
      img.alt = '';
      zone.appendChild(img);
    }

    var existingCheck = zone.querySelector('.vto-dropzone-check');
    if (!existingCheck) {
      var check = document.createElement('div');
      check.className = 'vto-dropzone-check';
      check.textContent = '\u2713';
      zone.appendChild(check);
    }

    this.updateGenerateBtn();
  }

  updateGenerateBtn() {
    const btn = this.drawer.querySelector('.vto-generate-btn');
    btn.disabled = !(this.fullBodyFile && this.selfieFile);
  }

  open() {
    this.drawer.classList.add('vto-drawer--open');
    document.body.style.overflow = 'hidden';
    this.drawer.querySelector('.vto-header-title').textContent = this.productTitle;
  }

  close() {
    this.drawer.classList.remove('vto-drawer--open');
    document.body.style.overflow = '';
  }

  async generate() {
    if (!this.fullBodyFile || !this.selfieFile || this.generating) return;
    this.generating = true;

    const body = this.drawer.querySelector('.vto-body');
    body.innerHTML = `
      <div class="vto-loading">
        <div class="vto-spinner"></div>
        <p>Generating your try-on...</p>
        <p style="font-size:11px;color:var(--color-foreground-secondary,#999);margin:0;">15\u201330 seconds</p>
      </div>
    `;

    try {
      const garmentRes = await fetch(this.productImage);
      const garmentBlob = await garmentRes.blob();
      const garmentFile = new File([garmentBlob], 'garment.jpg', { type: garmentBlob.type || 'image/jpeg' });

      const formData = new FormData();
      formData.append('personImage', this.fullBodyFile);
      formData.append('faceImage', this.selfieFile);
      formData.append('garmentImage', garmentFile);

      const res = await fetch(`${this.apiUrl}/api/try-on/image`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        let error = 'Generation failed';
        try { const json = JSON.parse(text); error = json.error || error; } catch {}
        throw new Error(error);
      }

      const blob = await res.blob();
      this.resultUrl = URL.createObjectURL(blob);
      this.showResult();
    } catch (err) {
      body.innerHTML = `
        <div class="vto-uploads" data-restore></div>
        <button class="vto-generate-btn" data-restore>Generate AI Try-On</button>
        <p class="vto-hint" data-restore>Stand against a plain background with good lighting</p>
        <div class="vto-error">
          <p>${err.message || 'Something went wrong'}</p>
          <button class="vto-retry-btn" onclick="this.closest('.vto-drawer') && this.closest('.vto-drawer').__vto__.retry()">Try Again</button>
        </div>
      `;
      this.restoreDropzones();
    } finally {
      this.generating = false;
    }
  }

  restoreDropzones() {
    const uploads = this.drawer.querySelector('.vto-uploads[data-restore]');
    if (!uploads) return;
    uploads.removeAttribute('data-restore');
    uploads.innerHTML = '';
    const fullBodyZone = this.createDropzone('fullbody');
    const selfieZone = this.createDropzone('selfie');
    uploads.appendChild(fullBodyZone);
    uploads.appendChild(selfieZone);
    this.setupDropzone(fullBodyZone, 'fullbody');
    this.setupDropzone(selfieZone, 'selfie');

    if (this.fullBodyFile) this.setFile('fullbody', this.fullBodyFile, fullBodyZone);
    if (this.selfieFile) this.setFile('selfie', this.selfieFile, selfieZone);

    const btn = this.drawer.querySelector('.vto-generate-btn[data-restore]');
    if (btn) {
      btn.removeAttribute('data-restore');
      btn.addEventListener('click', () => this.generate());
    }

    const hints = this.drawer.querySelectorAll('.vto-hint[data-restore]');
    hints.forEach((h) => h.removeAttribute('data-restore'));
  }

  createDropzone(type) {
    const zone = document.createElement('div');
    zone.className = `vto-dropzone vto-dropzone--${type}`;
    zone.setAttribute('data-slot', type);
    const label = type === 'fullbody' ? 'Full Body' : 'Selfie';
    const captureAttr = type === 'selfie' ? 'capture="user"' : 'capture="environment"';
    zone.innerHTML = `
      <div class="vto-dropzone-icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
      </div>
      <p class="vto-dropzone-label">${label}</p>
      <p class="vto-dropzone-hint">Tap to add photo</p>
      <input type="file" accept="image/*" ${captureAttr} class="vto-input--camera" hidden>
      <input type="file" accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.HEIC,.HEIF" class="vto-input--file" hidden>
    `;
    return zone;
  }

  showResult() {
    const body = this.drawer.querySelector('.vto-body');
    body.innerHTML = `
      <div class="vto-uploads" data-restore></div>
      <button class="vto-generate-btn" data-restore>Generate AI Try-On</button>
      <p class="vto-hint" data-restore>Stand against a plain background with good lighting</p>
      <div class="vto-result">
        <img src="${this.resultUrl}" alt="AI try-on result">
        <div class="vto-result-actions">
          <button class="vto-download-btn" onclick="this.closest('.vto-drawer').__vto__.download()">Download</button>
          <button class="vto-cart-btn" onclick="this.closest('.vto-drawer').__vto__.addToCart()">Add to Cart</button>
        </div>
      </div>
    `;
    this.restoreDropzones();
  }

  download() {
    if (!this.resultUrl) return;
    const a = document.createElement('a');
    a.href = this.resultUrl;
    a.download = 'aeternum-try-on.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  addToCart() {
    const variantId = this.getVariantId();
    if (!variantId) {
      const body = this.drawer.querySelector('.vto-body');
      const div = document.createElement('div');
      div.className = 'vto-error';
      div.textContent = 'Please select a size first';
      body.appendChild(div);
      return;
    }
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    })
    .then((res) => {
      if (!res.ok) throw new Error('Failed to add to cart');
      window.dispatchEvent(new CustomEvent('cart:updated'));
      this.close();
    })
    .catch((err) => {
      const body = this.drawer.querySelector('.vto-body');
      const existing = body.querySelector('.vto-error');
      if (existing) existing.remove();
      const div = document.createElement('div');
      div.className = 'vto-error';
      div.textContent = err.message || 'Failed to add to cart';
      body.appendChild(div);
    });
  }

  getVariantId() {
    const input = document.querySelector('input[name="id"][value]:not([value=""])');
    if (input && input.value) return input.value;
    const radio = document.querySelector('input[name="id"]:checked');
    if (radio && radio.value) return radio.value;
    const params = new URLSearchParams(window.location.search);
    const variant = params.get('variant');
    if (variant) return variant;
    return null;
  }

  retry() {
    this.generate();
  }
}

document.querySelectorAll('.vto-trigger').forEach((btn) => {
  const vto = new VirtualTryOn(btn);
});
